import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import SyncedEvents, { addDays } from './SyncedEvents';
import WeekDinners from './WeekDinners';
import ShoppingList from './ShoppingList';
import ChoreBoard, { todayYmd } from './ChoreBoard';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mondayOf = (ymd) => {
  const d = new Date(`${ymd}T12:00:00`);
  const m = d.getDay() === 0 ? 6 : d.getDay() - 1;
  d.setDate(d.getDate() - m);
  return d.toISOString().slice(0, 10);
};

const shiftWeeks = (ymd, w) => {
  const d = new Date(`${ymd}T12:00:00`);
  d.setDate(d.getDate() + w * 7);
  return d.toISOString().slice(0, 10);
};

const dayLabel = (mondayStr, offset) => {
  const d = new Date(`${mondayStr}T12:00:00`);
  d.setDate(d.getDate() + offset);
  return d.getDate();
};

const monthLabel = (mondayStr) => {
  const d = new Date(`${mondayStr}T12:00:00`);
  return d.toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' }).toUpperCase();
};

const DAYS = [
  { k: 'mon', label: 'MÅN', offset: 0 },
  { k: 'tue', label: 'TIS', offset: 1 },
  { k: 'wed', label: 'ONS', offset: 2 },
  { k: 'thu', label: 'TOR', offset: 3 },
  { k: 'fri', label: 'FRE', offset: 4 },
  { k: 'sat', label: 'LÖR', offset: 5 },
  { k: 'sun', label: 'SÖN', offset: 6 },
];

function useDebouncedSave(save, delay = 700) {
  const pending = useRef({});
  const timer = useRef(null);
  return useCallback((partial) => {
    pending.current = { ...pending.current, ...partial };
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const toSend = pending.current;
      pending.current = {};
      timer.current = null;
      save(toSend);
    }, delay);
  }, [save, delay]);
}

export default function AmethystWeeklyPlan({ day, setDay }) {
  const [data, setData] = useState(null);
  const monday = mondayOf(day);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/mysida/weekly-planner?date=${monday}`, { withCredentials: true });
      setData(r.data);
    } catch {}
  }, [monday]);
  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (patch) => {
    try {
      await axios.put(`${API}/mysida/weekly-planner/${monday}`, patch, { withCredentials: true });
    } catch {}
  }, [monday]);

  const dsave = useDebouncedSave(save, 700);

  if (!data) return <div className="text-center py-16 opacity-60">Laddar…</div>;

  const setDay_ = (k, v) => {
    const days = { ...data.days, [k]: v };
    setData((p) => ({ ...p, days }));
    dsave({ days });
  };
  const setDinner = (k, v) => {
    const dinners = { ...data.dinners, [k]: v };
    setData((p) => ({ ...p, dinners }));
    dsave({ dinners });
  };
  const setDinners = (patch) => {
    const dinners = { ...data.dinners, ...patch };
    setData((p) => ({ ...p, dinners }));
    save({ dinners });
  };
  const setRemind = (v) => { setData((p) => ({ ...p, dinner_remind: v })); save({ dinner_remind: v }); };
  const setShopDays = (v) => { setData((p) => ({ ...p, shop_days: v })); save({ shop_days: v }); };
  const setNotes = (v) => { setData((p) => ({ ...p, notes: v })); dsave({ notes: v }); };

  const DayBox = ({ dk, className = '', highlight = false }) => (
    <div className={`border-2 border-[#C8B6FF] rounded-md overflow-hidden bg-white flex ${className}`}>
      <div className={`w-10 flex-shrink-0 flex flex-col items-center pt-2 gap-1 ${highlight ? 'bg-[#DED0F0]' : 'bg-[#F5EEFF]'}`}>
        <div className="text-[10px] font-black tracking-widest text-[#7D6BA0]">{dk.label}</div>
        <div className="border-2 border-[#8B7BAA] rounded-md w-6 h-6 flex items-center justify-center font-black text-xs text-[#4E4370]">
          {dayLabel(monday, dk.offset)}
        </div>
      </div>
      <textarea
        value={data.days?.[dk.k] || ''}
        onChange={(e) => setDay_(dk.k, e.target.value)}
        className="flex-1 p-2 text-sm outline-none resize-none bg-transparent"
        placeholder=""
        data-testid={`weekly-day-${dk.k}`}
      />
    </div>
  );

  return (
    <div className="neo-card bg-white p-4 md:p-6" data-testid="amethyst-weekly">
      <SyncedEvents from={monday} to={addDays(monday, 6)} title="Veckans händelser från kalendern och månadsöversikten" testid="weekly-synced-events" />
      {/* Header */}
      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-2xl sm:text-3xl font-black tracking-[0.2em] text-[#7D6BA0]">WEEKLY&nbsp;&nbsp;PLAN</div>
          <div className="text-[11px] uppercase tracking-widest text-[#8B7BAA] mt-0.5" data-testid="weekly-month-label">{monthLabel(monday)}</div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setDay(shiftWeeks(monday, -1))} className="neo-btn p-1.5 bg-white" data-testid="weekly-prev-btn"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setDay(mondayOf(new Date().toISOString().slice(0,10)))} className="neo-btn px-2 py-1 bg-[#E8DCF5] text-xs font-bold" data-testid="weekly-today-btn">denna vecka</button>
          <button onClick={() => setDay(shiftWeeks(monday, 1))} className="neo-btn p-1.5 bg-white" data-testid="weekly-next-btn"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <WeekDinners monday={monday} data={data} setDinner={setDinner} setRemind={setRemind} setShopDays={setShopDays} setDinners={setDinners} />
      <ShoppingList monday={monday} dinnersKey={JSON.stringify(data.dinners || {})} />
      <div className="mb-4">
        <ChoreBoard date={todayYmd() >= monday && todayYmd() <= addDays(monday, 6) ? todayYmd() : monday} freqs={['week']} title="Veckans sysslor" />
      </div>
      {/* 3x3 Amethyst grid: Mon-Wed top, Thu-Sat middle, Sun+Notes bottom */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
        {[0, 1, 2].map((i) => <React.Fragment key={i}>{DayBox({ dk: DAYS[i], className: 'min-h-[150px]' })}</React.Fragment>)}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
        {[3, 4, 5].map((i) => <React.Fragment key={i}>{DayBox({ dk: DAYS[i], className: 'min-h-[150px]' })}</React.Fragment>)}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {DayBox({ dk: DAYS[6], className: 'min-h-[150px]' })}
        <div
          className="col-span-1 md:col-span-2 border-2 border-[#C8B6FF] rounded-md overflow-hidden bg-white flex"
          style={{ minHeight: 150 }}
        >
          <div className="w-14 flex-shrink-0 bg-[#F5EEFF] flex items-start justify-center pt-2">
            <div className="text-[10px] font-black tracking-widest text-[#7D6BA0]">NOTES</div>
          </div>
          <textarea
            value={data.notes || ''}
            onChange={(e) => setNotes(e.target.value)}
            className="flex-1 outline-none resize-none text-sm p-2"
            style={{ backgroundImage: 'linear-gradient(#C8B6FF 1px, transparent 1px), linear-gradient(90deg, #C8B6FF 1px, transparent 1px)', backgroundSize: '18px 18px' }}
            data-testid="weekly-notes"
          />
        </div>
      </div>
    </div>
  );
}
